import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import {
  Button, DataTable, EmptyState, FilterBar, FilterChips, FormGrid, MaisFiltros, MaisFiltrosGrupo, Pagination, Pill, SelectField, Tabs,
  type Column, type MaisFiltrosResumo,
} from "@atlas/ui/web";
import type {
  AdminCategoriaLegal, AdminOrigemVerba, AdminPrefeitura, AdminRubrica, AdminRubricaBucket,
  AdminRubricaNatureza, AdminRubricaVinculo,
} from "@atlas/sdk";
import { categoriaDaNatureza } from "@atlas/domain";
import { classeDaRubrica, vinculosAtivosPorCodigo } from "./classificacao-campos";
import { atlas } from "../../lib/sdk";
import { QUERY_UNIDADES_OPERAVEIS, listarUnidadesOperaveis } from "../../lib/unidades-operaveis";
import { aplicarFiltros, contagens, motivoVazio } from "../../lib/recorte";
import { useFiltros } from "../../lib/use-filtros";
import { Corta, Modal } from "../prefeitura/_ui";
import { Conciliacao, HistoricoRubricas, ObrigacoesLegadas } from "./motor-rubricas-abas";
// Extraido em 02/09/2026 para que a tela de trava de margem pudesse hospedar o
// mesmo formulario em vez de virar a terceira copia dele. Daqui continua sendo
// usado com `vinculoAtivo` por prop — nada mudou de comportamento.
import { ModalClassificacao } from "./motor-rubricas-classificar";
// Rotulos e formatadores moram fora dos dois arquivos: o classificador usa os
// mesmos, e importa-los daqui fecharia um ciclo.
import {
  CATEGORIA_LABEL, CATEGORIA_PILL, PRODUTO_LABEL, descreverRegraDoOrgao, fmtCompetencia, fmtVigencia,
  rotuloTipoServico,
} from "./motor-rubricas-labels";

/** As duas categorias de consignação: as que têm consignatária e tipo de serviço. */
const ehConsignacao = (c: AdminCategoriaLegal) => c === "consignacao_credito" || c === "consignacao_facultativa";
/** A sugestão do arquivo, no vocabulário da categoria (a descoberta ainda fala natureza). */
const rotuloSugestao = (n: AdminRubricaNatureza) => CATEGORIA_LABEL[categoriaDaNatureza(n)];

/**
 * MOTOR DE RUBRICAS — o que cada código da folha significa, e desde quando.
 *
 * A rubrica é o roteador do dinheiro: é por ela que a prefeitura sabe para quem
 * mandar o repasse, e é dela que a Atlas deriva o banco de uma dívida legada
 * enquanto a carteira oficial do banco não chega.
 *
 * Substitui a `rubricas.tsx` anterior, que era uma página única de 573 linhas
 * com catálogo, importação e conciliação empilhados, sem busca, sem filtro, sem
 * paginação e com erro em `alert()`. As três coisas eram diferentes e disputavam
 * a mesma tela.
 *
 * A ABA FICA NA URL (`?aba=`) para o link ser compartilhável — "olha a pendência
 * dessa folha" precisa ser um link, não um roteiro de cliques.
 *
 * A IMPORTAÇÃO NÃO ENTRA AQUI. O pacote de retorno continua em Troca de
 * arquivos, e o relatório de conciliação em `/averbadora/rubricas/conciliacao`:
 * misturar o upload bruto com a classificação foi o que tornou a tela antiga
 * ilegível.
 */

const ABAS = [
  { key: "catalogo", label: "Catálogo" },
  { key: "pendentes", label: "Pendentes de vínculo" },
  { key: "obrigacoes", label: "Obrigações legadas" },
  { key: "conciliacao", label: "Conciliação" },
  { key: "historico", label: "Histórico" },
];

/** A competência corrente, para pré-preencher a troca de vigência. */
function competenciaAtual(): string {
  const d = new Date();
  return `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, "0")}`;
}

const GRADE_FILTROS: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
  gap: 10,
};

export function AdminMotorRubricas() {
  const [params, setParams] = useSearchParams();
  const aba = params.get("aba") ?? "catalogo";
  const folhaDaUrl = Number(params.get("folha")) || null;

  const prefeituras = useQuery({
    queryKey: QUERY_UNIDADES_OPERAVEIS,
    queryFn: listarUnidadesOperaveis,
  });

  const [folhaId, setFolhaId] = useState<number | null>(folhaDaUrl);

  const trocar = (patch: Record<string, string>) => {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v) p.set(k, v); else p.delete(k);
    }
    setParams(p, { replace: true });
  };

  const q = useQuery({
    queryKey: ["admin", "rubricas", folhaId],
    queryFn: () => atlas.admin.rubricas.list(folhaId!),
    enabled: folhaId != null,
  });

  return (
    <div style={{ display: "grid", gap: 14 }}>
      <div>
        <span style={{ fontSize: 12, letterSpacing: "0.1em", fontWeight: 700, color: "var(--text-dim)", textTransform: "uppercase" }}>
          Averbadora
        </span>
        <h1 style={{ margin: "4px 0 0", fontSize: "1.6rem" }}>Motor de rubricas</h1>
        <p style={{ margin: "4px 0 0", fontSize: 13, color: "var(--text-muted)", maxWidth: 760 }}>
          O arquivo do órgão diz <em>“matrícula 70001, código 0450, R$ 480 descontados”</em> — e nada mais.
          Não diz se é empréstimo, de quem é o dinheiro, nem qual margem ele consome. É aqui que uma pessoa
          declara isso, uma vez por código de cada folha.
        </p>
        {/* O link para a importação em lote NÃO fica aqui.
            Ele é um passo de ONBOARDING — usado uma vez por folha, quando o
            catálogo dela está vazio — e um link sempre visível no cabeçalho dava a
            ele a permanência de uma tarefa do dia a dia. Foi para dentro da aba
            Pendentes, que é exatamente onde alguém está quando ele resolve algo, e
            para o Catálogo na forma de conferência periódica. */}
      </div>

      <SeletorDeFolha
        prefeituras={prefeituras.data?.prefeituras ?? []}
        carregando={prefeituras.isLoading}
        folhaId={folhaId}
        onFolha={(id) => { setFolhaId(id); trocar({ folha: id ? String(id) : "" }); }}
      />

      {folhaId != null ? <RegraDoOrgao unidadeId={folhaId} /> : null}

      {folhaId == null ? (
        <div style={{ padding: 36, textAlign: "center", color: "var(--text-muted)", border: "1px solid var(--border)", borderRadius: 12 }}>
          Escolha a folha para ver o catálogo dela. A mesma rubrica significa coisas diferentes em folhas diferentes.
        </div>
      ) : (
        <>
          <Tabs
            tabs={ABAS.map((a) => a.key === "pendentes" && (q.data?.pendentes ?? 0) > 0
              ? { ...a, badge: <Pill variant="pendente">{q.data!.pendentes}</Pill> }
              : a)}
            activeKey={aba}
            onChange={(k) => trocar({ aba: k })}
          />

          {q.isError ? (
            <div style={{ padding: 14, borderRadius: 10, fontSize: 13, background: "color-mix(in srgb, var(--danger-500) 12%, transparent)" }}>
              Não foi possível carregar o catálogo desta folha.
            </div>
          ) : aba === "pendentes" ? (
            <Pendentes folhaId={folhaId} dados={q.data} carregando={q.isLoading} />
          ) : aba === "obrigacoes" ? (
            <ObrigacoesLegadas folhaId={folhaId} />
          ) : aba === "conciliacao" ? (
            <Conciliacao folhaId={folhaId} />
          ) : aba === "historico" ? (
            <HistoricoRubricas />
          ) : (
            <Catalogo folhaId={folhaId} dados={q.data} carregando={q.isLoading} />
          )}
        </>
      )}
    </div>
  );
}

// ============================================================ seletor de folha

/**
 * Município → Folha, dois níveis.
 *
 * Cada folha é um cadastro próprio (Morada Nova tem três: a Administração
 * Direta, a A.M.T e o I.M.A.M.N.), e o agrupamento sai de `municipioIbge`.
 * Mostrar as folhas numa lista plana faria três pagadoras do mesmo município
 * parecerem três municípios — e é justamente por confundir isso que alguém
 * classificaria a rubrica da folha errada.
 */
function SeletorDeFolha({ prefeituras, carregando, folhaId, onFolha }: {
  prefeituras: AdminPrefeitura[];
  carregando: boolean;
  folhaId: number | null;
  onFolha: (id: number | null) => void;
}) {
  const municipios = useMemo(() => {
    const mapa = new Map<string, { rotulo: string; folhas: AdminPrefeitura[] }>();
    for (const p of prefeituras) {
      // Agrupa por IBGE quando ele existe; pelo nome do município quando não.
      // Cadastro sem IBGE é raro mas acontece (a busca por CNPJ falha em
      // distrito novo) e cair num grupo "sem município" é melhor que sumir.
      const chave = p.municipioIbge > 0 ? String(p.municipioIbge) : (p.municipioNome || "—");
      const rotulo = p.municipioNome || (p.municipioIbge > 0 ? `IBGE ${p.municipioIbge}` : "Sem município");
      if (!mapa.has(chave)) mapa.set(chave, { rotulo, folhas: [] });
      mapa.get(chave)!.folhas.push(p);
    }
    return [...mapa.values()].sort((a, b) => a.rotulo.localeCompare(b.rotulo));
  }, [prefeituras]);

  const daFolha = prefeituras.find((p) => p.id === folhaId);

  return (
    <div style={{ padding: 12, background: "var(--bg-elev-2)", borderRadius: 10, border: "1px solid var(--border)" }}>
      <FormGrid cols={2}>
        <SelectField
          label="Folha (unidade pagadora)"
          value={folhaId != null ? String(folhaId) : ""}
          onChange={(e) => onFolha(Number(e.target.value) || null)}
          options={[
            { value: "", label: carregando ? "carregando…" : "— escolha a folha —" },
            // `optgroup` não existe no SelectField; o município vira prefixo do
            // rótulo. Menos elegante e igualmente inequívoco.
            ...municipios.flatMap((m) => m.folhas.map((f) => ({
              value: String(f.id),
              label: `${m.rotulo} · ${f.nome}`,
            }))),
          ]}
          hint="A rubrica é por folha: o mesmo código pode ser de consignatárias diferentes em folhas do mesmo município."
        />
        {daFolha ? (
          <div style={{ display: "grid", gap: 2, alignSelf: "end", paddingBottom: 6 }}>
            <span style={{ fontSize: 11, color: "var(--text-dim)", textTransform: "uppercase", letterSpacing: ".06em" }}>
              Selecionada
            </span>
            <span style={{ fontSize: 13 }}>{daFolha.nome}</span>
            <span style={{ fontSize: 11, color: "var(--text-muted)" }}>
              {daFolha.municipioNome ? `${daFolha.municipioNome} · ` : ""}{daFolha.uf}
            </span>
          </div>
        ) : null}
      </FormGrid>
    </div>
  );
}

// ============================================================ regra do órgão

/**
 * A REGRA DE MARGEM DO ÓRGÃO, lida do cadastro — nunca de constante.
 *
 * Até a issue #176 o percentual morava no nome do produto ("Empréstimo (35%)"),
 * e quem classificava as rubricas da ALECE lia que a ALECE trabalha com 35/5/5.
 * Não trabalha: a configuração dela é margem informada, saldo compartilhado. O
 * produto diz QUAL margem o desconto ocupa; quanto ela vale é do órgão (ADR-0012).
 *
 * Mesmas chaves da ficha da organização e do painel de margem informada: editar
 * a regra lá atualiza esta linha. Sem os dados, nenhum número — um percentual
 * provisório seria exatamente o defeito que esta linha existe para desfazer.
 */
function RegraDoOrgao({ unidadeId }: { unidadeId: number }) {
  const regra = useQuery({
    queryKey: ["admin", "regra-margem", "organizacao", unidadeId],
    queryFn: () => atlas.admin.regraMargemDaOrganizacao(unidadeId),
  });
  const resumo = useQuery({
    queryKey: ["admin", "margem-informada", unidadeId, "resumo"],
    queryFn: () => atlas.admin.margemInformada.resumo(unidadeId),
  });

  const texto = regra.isLoading || resumo.isLoading
    ? "carregando a regra de margem…"
    : regra.isError || resumo.isError || !regra.data
      ? "não foi possível carregar a regra de margem deste órgão"
      : descreverRegraDoOrgao(regra.data, resumo.data?.configuracoes ?? []);

  return (
    <div
      role="note"
      style={{
        display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap",
        padding: "8px 12px", borderRadius: 10, fontSize: 13,
        border: "1px solid var(--border)", background: "var(--bg-elev)",
      }}
    >
      <span style={{ fontSize: 11, color: "var(--text-dim)", textTransform: "uppercase", letterSpacing: ".06em" }}>
        Margem deste órgão
      </span>
      <span data-testid="regra-do-orgao">{texto}</span>
      <Link to={`/averbadora/organizacoes/${unidadeId}?aba=margem`} style={{ fontSize: 12, marginLeft: "auto" }}>
        ver regra →
      </Link>
    </div>
  );
}

// ============================================================ Catálogo

type DadosRubricas = Awaited<ReturnType<typeof atlas.admin.rubricas.list>> | undefined;

function Catalogo({ folhaId, dados, carregando }: { folhaId: number; dados: DadosRubricas; carregando: boolean }) {
  const [busca, setBusca] = useState("");
  const [categoria, setCategoria] = useState("");
  const [credor, setCredor] = useState("");
  const [produto, setProduto] = useState("");
  const [situacao, setSituacao] = useState("");
  // Item 6: por padrao mostra so' as CONSIGNACOES — credito e facultativa,
  // classificadas ou sugeridas pelo consignados. A facultativa entrou na #270: a
  // regra do orgao pode faze-la ocupar margem (#271), e e' ela que agora pede
  // tipo de servico. O resto do holerite (provento,
  // previdencia, imposto, sindicato) fica escondido atras do "Todas as verbas".
  // A absorcao segue guardando tudo; isto e' so' a visao padrao, pra prefeitura
  // grande nao afogar o operador em verba que nao vira divida.
  const [soConsignacao, setSoConsignacao] = useState(true);
  const [pagina, setPagina] = useState(1);
  const [porPagina, setPorPagina] = useState(25);
  const [editando, setEditando] = useState<AdminRubrica | "nova" | null>(null);
  const [vendoHistorico, setVendoHistorico] = useState<AdminRubrica | null>(null);

  const rubricas = dados?.rubricas ?? [];
  const vinculos = dados?.vinculos ?? [];

  /** A vigência ATIVA de cada rubrica, por código canônico. */
  const ativoPorCodigo = useMemo(() => vinculosAtivosPorCodigo(vinculos), [vinculos]);

  // O que cada linha É vem do VÍNCULO ATIVO (#270): categoria legal, tipo de
  // serviço, desconto obrigatório. A rubrica só conhece a natureza antiga.
  const classes = useMemo(
    () => new Map(rubricas.map((r) => [r.id, classeDaRubrica(r, ativoPorCodigo.get(r.codigoVerba))])),
    [rubricas, ativoPorCodigo],
  );
  const cl = (r: AdminRubrica) => classes.get(r.id) ?? classeDaRubrica(r, undefined);

  const vigenciasDe = (codigo: string) => vinculos.filter((v) => v.codigoVerba === codigo);

  /** Quem declara cada verba — por código canônico. */
  const origemPorCodigo = useMemo(() => {
    const m = new Map<string, AdminOrigemVerba>();
    for (const o of dados?.origens ?? []) m.set(o.codigoVerba, o);
    return m;
  }, [dados]);

  // `escopo` (o "Exibir") tem default NAO-VAZIO: nasce em "so' consignacao", e
  // por isso `ligado` e' o desvio do default e `limpar` RESTAURA `true` em vez
  // de esvaziar. Era o que o reset a mao ja fazia; agora esta declarado.
  //
  // A dimensao tambem entrou no inventario neste passo: ela filtrava desde
  // sempre e `filtrosRapidos` nao a listava — o G9 acusou na hora em que a tela
  // adotou o hook, que e' exatamente para isso que ele existe.
  const filtros = useFiltros(
    [
      { nome: "busca", ligado: busca !== "", limpar: () => setBusca("") },
      { nome: "escopo", ligado: !soConsignacao, limpar: () => setSoConsignacao(true) },
      { nome: "situacao", ligado: situacao !== "", limpar: () => setSituacao("") },
      { nome: "categoria", ligado: categoria !== "", limpar: () => setCategoria("") },
      { nome: "credor", ligado: credor !== "", limpar: () => setCredor("") },
      { nome: "produto", ligado: produto !== "", limpar: () => setProduto("") },
    ],
    setPagina,
  );
  const { filtrar } = filtros;

  // Filtro no cliente de propósito: uma folha tem DEZENAS de rubricas (o
  // I.M.A.M.N. real tem 20 em três competências), não milhares. Paginar no
  // servidor aqui seria complexidade sem ganho, e o filtro instantâneo é melhor
  // para quem está procurando um código que não lembra direito.
  /**
   * A CONTRADICAO ENTRE "EXIBIR" E AS OUTRAS DIMENSOES, resolvida na raiz.
   *
   * Os predicados eram independentes e AND-ados, e isso produzia combinacoes
   * MATEMATICAMENTE IMPOSSIVEIS que a tela aceitava e devolvia vazio sem
   * explicar. Eram tres, todas encontradas na auditoria de 04/09/2026:
   *
   *   1. Situacao "consignacao sem produto" com Natureza != consignacao — o
   *      predicado da situacao exige `natureza === "consignacao"`.
   *   2. Consignataria escolhida com Natureza != consignacao — a propria coluna
   *      renderiza "nao se aplica" fora de consignacao.
   *   3. Produto/margem escolhido com Natureza != consignacao — idem.
   *
   * Mais o caso do "Exibir": com escopo em "so' consignacao" e Natureza em
   * "provento", sobreviviam apenas as rubricas que o consignados SUGERIU como
   * consignacao e um humano classificou como outra coisa — quase nunca alguma,
   * e o empty state dizia so' "nenhuma rubrica com esses filtros".
   *
   * A CORRECAO NAO E' DESABILITAR COMBINACAO A COMBINACAO. As opcoes de cada
   * dimensao passam a sair do DADO com semantica `exceto` (FR-010): a lista de
   * Natureza vem do conjunto que os OUTROS filtros ja recortaram, entao uma
   * natureza que nao existe dentro do escopo atual simplesmente nao aparece
   * como opcao. A combinacao impossivel deixa de ser alcancavel em vez de
   * virar zero linhas — e de graca as contagens ficam honestas.
   */
  const termo = busca.trim().toLowerCase();
  const predicados = useMemo(() => ({
    escopo: soConsignacao
      ? (r: AdminRubrica) =>
          ehConsignacao(cl(r).categoria) ||
          r.naturezaSugerida === "consignacao" ||
          r.naturezaSugerida === "consignacao_nao_crediticia"
      : null,
    categoria: categoria ? (r: AdminRubrica) => cl(r).categoria === categoria : null,
    credor: credor ? (r: AdminRubrica) => String(cl(r).consignatariaId ?? "") === credor : null,
    produto: produto ? (r: AdminRubrica) => (cl(r).produto ?? "") === produto : null,
    situacao: situacao
      ? (r: AdminRubrica) => {
          if (situacao === "confirmada") return r.confirmadaEm !== null;
          if (situacao === "pendente") return r.confirmadaEm === null;
          // "sem_produto" e' derivado: credito que ainda nao tem margem.
          return cl(r).categoria === "consignacao_credito" && cl(r).produto === null;
        }
      : null,
    busca: termo
      ? (r: AdminRubrica) =>
          r.codigoOriginal.toLowerCase().includes(termo) || r.descricao.toLowerCase().includes(termo)
      : null,
  }), [soConsignacao, categoria, credor, produto, situacao, termo, classes]);

  const filtradas = useMemo(
    () => aplicarFiltros(rubricas, predicados).sort((a, b) => a.codigoOriginal.localeCompare(b.codigoOriginal)),
    [rubricas, predicados],
  );

  /** Base de cada faceta: tudo menos o filtro da propria dimensao. */
  const semCategoria = useMemo(() => aplicarFiltros(rubricas, predicados, "categoria"), [rubricas, predicados]);
  const semCredor = useMemo(() => aplicarFiltros(rubricas, predicados, "credor"), [rubricas, predicados]);
  const semProduto = useMemo(() => aplicarFiltros(rubricas, predicados, "produto"), [rubricas, predicados]);
  const semSituacao = useMemo(() => aplicarFiltros(rubricas, predicados, "situacao"), [rubricas, predicados]);
  const semEscopo = useMemo(() => aplicarFiltros(rubricas, predicados, "escopo"), [rubricas, predicados]);

  const contCategoria = useMemo(() => contagens(semCategoria, (r) => cl(r).categoria), [semCategoria, classes]);
  const contCredor = useMemo(() => contagens(semCredor, (r) => String(cl(r).consignatariaId ?? "")), [semCredor, classes]);
  const contProduto = useMemo(() => contagens(semProduto, (r) => cl(r).produto ?? ""), [semProduto, classes]);
  const contSituacao = useMemo(
    () => contagens(semSituacao, (r) =>
      cl(r).categoria === "consignacao_credito" && cl(r).produto === null ? "sem_produto"
      : r.confirmadaEm !== null ? "confirmada"
      : "pendente"),
    [semSituacao, classes],
  );

  const pagina1 = (pagina - 1) * porPagina;
  const daPagina = filtradas.slice(pagina1, pagina1 + porPagina);

  const credores = useMemo(() => {
    const m = new Map<number, string>();
    for (const r of rubricas) if (r.credorId != null) m.set(r.credorId, r.consignataria ?? `Consignatária ${r.credorId}`);
    return [...m].sort((a, b) => a[1].localeCompare(b[1]));
  }, [rubricas]);

  // O que esta ligado DENTRO do painel, com valor legivel — e' o que aparece
  // como chip quando ele esta fechado, para o recorte nao ficar invisivel atras
  // de um botao. Os rotulos sao os MESMOS dos controles: chip e controle com
  // nomes diferentes viram duas dimensoes na cabeca do operador.
  //
  // Depende de `credores` (o mapa id -> nome), e por isso mora aqui e nao junto
  // do `useFiltros`.
  const resumoAvancado: MaisFiltrosResumo[] = [
    ...(categoria
      ? [{ rotulo: "Categoria", valor: CATEGORIA_LABEL[categoria as AdminCategoriaLegal], limpar: () => filtrar(setCategoria)("") }]
      : []),
    ...(credor
      ? [{ rotulo: "Consignatária", valor: credores.find(([id]) => String(id) === credor)?.[1] ?? credor, limpar: () => filtrar(setCredor)("") }]
      : []),
    ...(produto
      ? [{ rotulo: "Produto", valor: PRODUTO_LABEL[produto as AdminRubricaBucket], limpar: () => filtrar(setProduto)("") }]
      : []),
  ];

  const columns: Column<AdminRubrica>[] = [
    {
      key: "codigo",
      header: "Código",
      width: 100,
      mono: true,
      // `codigoOriginal`, com os zeros à esquerda: é a forma que a folha usa e a
      // que sai nos arquivos. O canônico (`234`) é só chave interna.
      render: (r) => <span style={{ fontWeight: 600 }}>{r.codigoOriginal}</span>,
    },
    {
      key: "descricao",
      header: "Descrição",
      width: 240,
      render: (r) => (
        <div style={{ display: "grid", gap: 2 }}>
          <Corta largura={228} titulo={r.descricao}>{r.descricao || "—"}</Corta>
          {r.descricaoMudouEm ? (
            <span style={{ fontSize: 11, color: "var(--gold-500)" }} title="A folha trocou o texto desta rubrica. Pode ser troca de consignatária — vale reconferir.">
              texto mudou na folha
            </span>
          ) : null}
        </div>
      ),
    },
    {
      key: "categoria",
      header: "Categoria",
      width: 170,
      // A pílula é o estado REAL. Rubrica não classificada com sugestão aparece
      // no escopo "consignações" por causa da sugestão — e a linha diz isso, em
      // vez de o agrupamento dar a entender que já está classificada (#176).
      render: (r) => {
        const c = cl(r).categoria;
        return (
          <span style={{ display: "inline-flex", flexDirection: "column", gap: 2 }}>
            <Pill variant={CATEGORIA_PILL[c]}>{CATEGORIA_LABEL[c]}</Pill>
            {c === "nao_classificada" && r.naturezaSugerida !== "nao_classificada" ? (
              <span style={{ fontSize: 11, color: "var(--text-dim)" }}>
                sugestão: {rotuloSugestao(r.naturezaSugerida)}
              </span>
            ) : null}
          </span>
        );
      },
    },
    {
      key: "tipoServico",
      header: "Tipo de serviço",
      width: 170,
      render: (r) => {
        const { categoria: c, tipoServico } = cl(r);
        if (!ehConsignacao(c)) return <span style={{ opacity: 0.4 }}>—</span>;
        // Legado anterior à #270: consignação ainda sem tipo. Fica à vista para
        // o operador saber o que falta — nada é preenchido sozinho.
        if (tipoServico === null) {
          return (
            <span title="Consignação classificada antes da #270, sem tipo de serviço. Edite a rubrica para escolher.">
              <Pill variant="pendente">a definir</Pill>
            </span>
          );
        }
        return <Corta largura={158} titulo={rotuloTipoServico(tipoServico)}>{rotuloTipoServico(tipoServico)}</Corta>;
      },
    },
    {
      key: "credor",
      header: "Consignatária",
      width: 170,
      render: (r) => !ehConsignacao(cl(r).categoria)
        ? <span style={{ opacity: 0.4 }}>não se aplica</span>
        : <Corta largura={158} titulo={r.consignataria ?? ""}>{r.consignataria || "—"}</Corta>,
    },
    {
      key: "produto",
      header: "Produto",
      width: 160,
      render: (r) => {
        const { categoria: c, produto: p } = cl(r);
        // Facultativa não tem produto: o que ela ocupa é a regra de margem do órgão (#271).
        if (c === "consignacao_facultativa") return <span style={{ fontSize: 12, color: "var(--text-dim)" }}>pela regra do órgão</span>;
        if (c !== "consignacao_credito") return <span style={{ opacity: 0.4 }}>—</span>;
        if (p === null) {
          return (
            <span title="Crédito sem produto não ocupa margem de nenhum dos três produtos. Não vira dívida.">
              <Pill variant="pendente">Sem produto</Pill>
            </span>
          );
        }
        return <span style={{ fontSize: 12 }}>{PRODUTO_LABEL[p]}</span>;
      },
    },
    {
      // POR QUE ESTE DESCONTO É DESTE BANCO — a pergunta que a tela não
      // respondia. Mostrava a classificação e o credor, mas não quem declarou
      // aquilo nem onde mexer para mudar. Desde a feature 004 o lugar de mexer
      // é o cadastro do convênio, e o link leva direto para lá.
      key: "origem",
      header: "Declarado por",
      width: 190,
      render: (r) => {
        const o = origemPorCodigo.get(r.codigoVerba);
        if (!o || o.origem === null) {
          return <span style={{ opacity: 0.5, fontSize: 12 }}>ninguém ainda</span>;
        }
        if (!o.semConvenio) {
          return (
            <div style={{ display: "grid", gap: 2 }}>
              <Link
                to="/averbadora/convenios"
                style={{ fontSize: 12, color: "var(--gold-500)" }}
                title={`Declarada pelo convênio ${o.convenioNome} — abrir o cadastro de convênios`}
              >
                <Corta largura={178} titulo={o.convenioNome ?? ""}>{o.convenioNome}</Corta>
              </Link>
              <span style={{ fontSize: 11, color: "var(--text-dim)" }}>{o.bancoNome ?? `Consignatária ${o.bancoId}`}</span>
            </div>
          );
        }
        return (
          <div style={{ display: "grid", gap: 2 }}>
            <span
              style={{ fontSize: 12, color: "var(--gold-500)" }}
              title={
                o.convenioId
                  ? `Declarada pelo convênio ${o.convenioId}, que não existe mais.`
                  : "Nenhum convênio explica esta verba: ela foi classificada à mão. O caminho normal é declarar no cadastro do convênio."
              }
            >
              {o.origem === "semeadura" ? "migração" : "à mão"} · sem convênio
            </span>
            <span style={{ fontSize: 11, color: "var(--text-dim)" }}>{o.bancoNome ?? "—"}</span>
          </div>
        );
      },
    },
    {
      key: "vigencia",
      header: "Vigência",
      width: 170,
      render: (r) => {
        const ativo = ativoPorCodigo.get(r.codigoVerba);
        const todas = vigenciasDe(r.codigoVerba);
        if (!ativo) return <span style={{ opacity: 0.5, fontSize: 12 }}>sem vigência ativa</span>;
        return (
          <div style={{ display: "grid", gap: 2 }}>
            <span style={{ fontSize: 12 }}>{fmtVigencia(ativo)}</span>
            {todas.length > 1 ? (
              <span style={{ fontSize: 11, color: "var(--text-dim)" }}>
                {todas.length - 1} vigência(s) anterior(es)
              </span>
            ) : null}
          </div>
        );
      },
    },
    {
      key: "situacao",
      header: "Situação",
      width: 120,
      render: (r) => r.confirmadaEm === null
        ? <Pill variant="pendente">Pendente</Pill>
        : <Pill variant="averbado">Confirmada</Pill>,
    },
    {
      key: "acoes",
      header: "Ações",
      width: 150,
      render: (r) => (
        <div style={{ display: "flex", gap: 4 }}>
          <Button size="sm" variant="ghost" title="Editar a classificação desta rubrica" onClick={() => setEditando(r)}>✎</Button>
          <Button size="sm" variant="ghost" title="Ver todas as vigências e quem as decidiu" onClick={() => setVendoHistorico(r)}>⧖</Button>
        </div>
      ),
    },
  ];

  return (
    <div style={{ display: "grid", gap: 12 }}>
      <ParaQueServe />
      <ResumoCatalogo dados={dados} />
      <DivergenciasConvenio dados={dados} />

      {/* PILOTO DO "MAIS FILTROS". Eram CINCO selects lado a lado — a barra
          deixava de ser varrivel e o operador passava a cacar o controle.
          Ficam VISIVEIS os dois que orientam a tarefa principal: o escopo
          (afeta margem ou tudo, que decide o universo) e a situacao (que decide
          a proxima acao). Descem para o painel as tres dimensoes de
          investigacao: natureza, consignataria e produto/margem.
          Nao e' compactar por estetica: esconder escopo ou situacao faria a tela
          filtrar sem dizer que filtra. */}
      <FilterBar
        searchValue={busca}
        onSearchChange={filtrar(setBusca)}
        {...filtros.propsFilterBar}
        actions={<Button size="sm" onClick={() => setEditando("nova")}>+ Nova rubrica</Button>}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 12, width: "100%" }}>
          <div style={GRADE_FILTROS}>
            {/* O reset RESTAURA "só consignação" em vez de esvaziar — e' o
                default operacional da tela, nao ausencia de filtro. Enquanto
                estiver em "Todas as verbas" o contador conta isto como um
                filtro ativo, que e' o que torna o desvio visivel. */}
            <SelectField
              label="Exibir"
              value={soConsignacao ? "consignacao" : "todas"}
              onChange={(e) => filtrar(setSoConsignacao)(e.target.value === "consignacao")}
              options={[
                { value: "consignacao", label: `Consignações — classificadas ou sugeridas · ${semEscopo.filter((r) => ehConsignacao(cl(r).categoria) || r.naturezaSugerida === "consignacao" || r.naturezaSugerida === "consignacao_nao_crediticia").length}` },
                { value: "todas", label: `Todas as verbas · ${semEscopo.length}` },
              ]}
            />
            {/* Situacao com as opcoes que EXISTEM no recorte. "Consignação sem
                produto" desaparece quando a Natureza escolhida nao e'
                consignacao — era uma das tres combinacoes garantidas de zero. */}
            <SelectField
              label="Situação" value={situacao} onChange={(e) => filtrar(setSituacao)(e.target.value)}
              options={[
                { value: "", label: `— TODAS — (${semSituacao.length})` },
                ...([
                  { value: "confirmada", label: "Confirmada" },
                  { value: "pendente", label: "Pendente de vínculo" },
                  { value: "sem_produto", label: "Crédito sem produto" },
                ] as const)
                  .filter((o) => (contSituacao[o.value] ?? 0) > 0 || situacao === o.value)
                  .map((o) => ({ value: o.value, label: `${o.label} (${contSituacao[o.value] ?? 0})` })),
              ]}
            />
          </div>

          <MaisFiltros ativos={resumoAvancado.length} resumo={resumoAvancado}>
            <MaisFiltrosGrupo titulo="Classificação">
              {/* As categorias listadas sao as que EXISTEM no recorte atual.
                  Com "Exibir" em "consignações", as outras categorias nao
                  aparecem — e' assim que a contradicao deixa de ser
                  alcancavel, em vez de ser aceita e devolver vazio. */}
              <SelectField
                label="Categoria" value={categoria} onChange={(e) => filtrar(setCategoria)(e.target.value)}
                options={[
                  { value: "", label: `— TODAS — (${semCategoria.length})` },
                  ...(Object.keys(CATEGORIA_LABEL) as AdminCategoriaLegal[])
                    .filter((k) => (contCategoria[k] ?? 0) > 0 || categoria === k)
                    .map((k) => ({ value: k, label: `${CATEGORIA_LABEL[k]} (${contCategoria[k] ?? 0})` })),
                ]}
              />
            </MaisFiltrosGrupo>
            <MaisFiltrosGrupo titulo="Vínculo com credor e produto">
              {/* "Consignatária" e' o termo do glossario desde a ADR-0015: a
                  consignataria de repasse, quem recebe. Quem presta o servico,
                  quando e' outro, e' o credor final. */}
              <SelectField
                label="Consignatária" value={credor} onChange={(e) => filtrar(setCredor)(e.target.value)}
                options={[
                  { value: "", label: `— TODAS — (${semCredor.length})` },
                  ...credores
                    .filter(([id]) => (contCredor[String(id)] ?? 0) > 0 || credor === String(id))
                    .map(([id, nome]) => ({ value: String(id), label: `${nome} (${contCredor[String(id)] ?? 0})` })),
                ]}
              />
              <SelectField
                label="Produto" value={produto} onChange={(e) => filtrar(setProduto)(e.target.value)}
                options={[
                  { value: "", label: `— TODOS — (${semProduto.length})` },
                  ...(Object.keys(PRODUTO_LABEL) as AdminRubricaBucket[])
                    .filter((b) => (contProduto[b] ?? 0) > 0 || produto === b)
                    .map((b) => ({ value: b, label: `${PRODUTO_LABEL[b]} (${contProduto[b] ?? 0})` })),
                ]}
              />
            </MaisFiltrosGrupo>
          </MaisFiltros>
        </div>
      </FilterBar>

      <DataTable
        columns={columns}
        rows={daPagina}
        rowKey={(r) => r.id}
        loading={carregando}
        emptyState={rubricas.length === 0
          ? "Nenhuma rubrica descoberta nesta folha ainda. Elas aparecem quando o primeiro pacote de retorno é importado em Troca de arquivos."
          : "Nenhuma rubrica encontrada com esses filtros."}
      />

      <Pagination
        total={filtradas.length}
        pagina={pagina}
        porPagina={porPagina}
        onPagina={setPagina}
        onPorPagina={setPorPagina}
        rotulo="rubricas"
      />

      {editando ? (
        <ModalClassificacao
          folhaId={folhaId}
          rubrica={editando === "nova" ? null : editando}
          vinculoAtivo={editando === "nova" ? null : ativoPorCodigo.get(editando.codigoVerba) ?? null}
          onClose={() => setEditando(null)}
        />
      ) : null}

      {vendoHistorico ? (
        <ModalHistorico
          rubrica={vendoHistorico}
          vigencias={vigenciasDe(vendoHistorico.codigoVerba)}
          onClose={() => setVendoHistorico(null)}
        />
      ) : null}
    </div>
  );
}

/**
 * O que o catálogo é, em quatro frases.
 *
 * Está na tela porque o próprio cliente que pediu o motor perguntou "não entendi
 * pra que serve o catálogo de rubricas". Quando quem encomendou não sabe o que a
 * tela faz, o problema é da tela, não de quem lê — e a explicação tem de estar
 * onde a dúvida aparece, não num documento à parte.
 *
 * O exemplo do `SAQUE ANTECIPCARD` não é decorativo: é o caso real que mostra por
 * que a classificação não pode ser adivinhada. Ele foi medido no arquivo do
 * I.M.A.M.N. e derrubou o classificador por texto (`bucketPorDescricao`).
 */
function ParaQueServe() {
  const [aberto, setAberto] = useState(false);
  return (
    <div style={{ padding: 12, borderRadius: 10, background: "var(--bg-elev-2)", border: "1px solid var(--border)", fontSize: 13 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
        <p style={{ margin: 0, maxWidth: 820 }}>
          Cada linha aqui é <strong>um código de desconto da folha</strong>, e o que uma pessoa decidiu sobre ele:
          se é consignação, para qual consignatária vai o repasse e qual margem ele consome. Sem essa decisão, o desconto{" "}
          <strong>não vira dívida de consignatária alguma</strong> — fica esperando na aba Pendentes.
        </p>
        <Button size="sm" variant="ghost" onClick={() => setAberto((v) => !v)}>
          {aberto ? "menos" : "por quê?"}
        </Button>
      </div>

      {aberto ? (
        <div style={{ marginTop: 10, display: "grid", gap: 8, color: "var(--text-muted)", maxWidth: 820 }}>
          <p style={{ margin: 0 }}>
            Porque <strong>nada disso pode ser lido do texto</strong> da rubrica. Três padrões que aparecem em
            qualquer folha:
          </p>
          <ul style={{ margin: 0, paddingLeft: 18, display: "grid", gap: 4 }}>
            <li>
              Uma verba de <strong>cartão</strong> costuma ser descrita como saque, antecipação ou adiantamento —
              e lida como empréstimo. Classificada errado, come a margem do produto errado: o servidor perde espaço
              para um empréstimo que poderia ter tomado.
            </li>
            <li>
              <strong>Previdência, imposto de renda e mensalidade de sindicato</strong> não são consignação
              nenhuma. Se virassem dívida, seriam cobradas como se fossem um empréstimo consignado.
            </li>
            <li>
              Há verba que <strong>traz o nome da consignatária na descrição</strong> — e ainda assim não basta: a razão
              social por extenso e o nome curto são a mesma coisa para uma pessoa e duas para o sistema. O credor
              tem que ser um <strong>registro</strong>, não um nome.
            </li>
          </ul>
          <p style={{ margin: 0 }}>
            E é <strong>por folha</strong>: um município costuma ter mais de uma — a administração direta, o
            instituto de previdência, uma autarquia — e a mesma consignatária costuma ter um código de verba diferente em
            cada uma. O mesmo número significa coisas diferentes em cada folha.
          </p>
          {/* A CONFERÊNCIA é o uso que sobrevive à migração. Reimportar o relatório
              não sobrescreve decisão confirmada — se a consignatária declarada
              diverge da confirmada, ele registra a divergência, que é o contador
              "Divergências" acima. É assim que se pega "a prefeitura trocou a
              consignatária de uma verba e não avisou". */}
          <p style={{ margin: 0, paddingTop: 6, borderTop: "1px solid var(--border)" }}>
            Depois da migração, dá para{" "}
            <Link to="/averbadora/rubricas/conciliacao" style={{ color: "var(--gold-500)" }}>
              conferir o catálogo contra um relatório novo do órgão
            </Link>
            . Reimportar não sobrescreve o que já foi decidido — acusa divergência quando a consignatária
            declarada não é a que está confirmada aqui. É como se descobre que o órgão trocou a consignatária de uma
            verba e não avisou.
          </p>
        </div>
      ) : null}
    </div>
  );
}

/** Os quatro números que decidem se a folha pode ir ao ar. */
function ResumoCatalogo({ dados }: { dados: DadosRubricas }) {
  if (!dados) return null;
  const cartoes: { rotulo: string; valor: number; alerta: boolean; dica: string }[] = [
    {
      rotulo: "Pendentes de vínculo", valor: dados.pendentes, alerta: dados.pendentes > 0,
      dica: "Enquanto houver pendente, o desconto daquela rubrica cai em conciliação em vez de virar dívida.",
    },
    {
      rotulo: "Consignações confirmadas", valor: dados.consignacoesConfirmadas, alerta: false,
      dica: "Rubricas que geram dívida, com credor e produto decididos.",
    },
    {
      rotulo: "Sem produto", valor: dados.semBalde, alerta: dados.semBalde > 0,
      dica: "Consignação sem produto — é o caso do plano odontológico. Não vira dívida, e isso pode estar certo.",
    },
    {
      rotulo: "Divergências", valor: dados.divergencias, alerta: dados.divergencias > 0,
      dica: "O relatório de conciliação declarou uma consignatária diferente da confirmada aqui.",
    },
  ];
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10 }}>
      {cartoes.map((c) => (
        <div
          key={c.rotulo}
          title={c.dica}
          style={{
            padding: 12, borderRadius: 10, background: "var(--bg-elev-2)",
            border: `1px solid ${c.alerta ? "var(--gold-500)" : "var(--border)"}`,
          }}
        >
          <div style={{ fontSize: 11, color: "var(--text-dim)", textTransform: "uppercase", letterSpacing: ".06em" }}>
            {c.rotulo}
          </div>
          <div style={{ fontSize: 22, fontWeight: 700, marginTop: 2 }}>{c.valor}</div>
        </div>
      ))}
    </div>
  );
}

/**
 * Convênio apontando para a rubrica de outro banco.
 *
 * AVISO, nunca bloqueio: a prefeitura pode ter trocado a rubrica e não avisado,
 * e travar averbação por isso seria pior que o problema. Mas é o único
 * guarda-corpo contra o repasse ir para o banco errado, então precisa aparecer.
 */
function DivergenciasConvenio({ dados }: { dados: DadosRubricas }) {
  const ds = dados?.divergenciasConvenio ?? [];
  if (ds.length === 0) return null;
  return (
    <div style={{ display: "grid", gap: 6 }}>
      {ds.map((d, i) => (
        <div
          key={i}
          style={{
            padding: 10, borderRadius: 8, fontSize: 12,
            background: "color-mix(in srgb, var(--gold-500) 10%, transparent)",
            border: `1px solid ${d.tipo === "verba_nao_catalogada" ? "var(--border)" : "var(--gold-500)"}`,
          }}
        >
          <strong>{d.convenioNome ?? "Convênio"}</strong> · {d.mensagem}
        </div>
      ))}
    </div>
  );
}

// ============================================================ Pendentes

/**
 * Rubricas que apareceram nos arquivos e ninguém classificou.
 *
 * Enquanto uma rubrica está aqui, o desconto dela NÃO é atribuído a banco algum:
 * `classificarVerba` devolve `conciliacao` e o valor é abatido de todos os
 * baldes de margem. Só restringe, nunca libera — o oposto de adivinhar pela
 * descrição, que errava justamente nos casos reais (`SAQUE ANTECIPCARD` é cartão
 * e caía em empréstimo).
 */
/**
 * AS VERBAS QUE NENHUM CONVÊNIO EXPLICA, junto com o que está em quarentena.
 *
 * São o mesmo problema visto de dois lados: a verba sem convênio é a causa, e o
 * desconto em quarentena é a consequência — ele não vira dívida, não tem
 * credor, e o valor fica abatido de todos os baldes de margem enquanto espera.
 * Mostrá-los em telas diferentes fazia o operador tratar como duas filas o que
 * uma única declaração de convênio resolve.
 *
 * ORDENADO PELO VALOR, que é o que ordena o trabalho: a verba com R$ 40 mil
 * parados custa mais do que a com R$ 300, e a ordem alfabética escondia isso.
 */
function SemConvenio({ folhaId, origens }: { folhaId: number; origens: AdminOrigemVerba[] }) {
  const q = useQuery({
    queryKey: ["admin", "conciliacao", "pendencias", folhaId],
    queryFn: () => atlas.admin.trava.pendencias(folhaId),
  });

  const linhas = useMemo(() => {
    const valorPorVerba = new Map<string, { valor: number; servidores: number; descricao: string }>();
    for (const v of q.data?.verbas ?? []) {
      const canonico = v.codigo_verba.replace(/^0+/, "") || "0";
      valorPorVerba.set(canonico, { valor: v.valor, servidores: v.servidores, descricao: v.descricao });
    }
    // As duas pontas: a verba sem convênio (mesmo já classificada à mão) e a
    // verba que só existe na quarentena — esta ainda não virou rubrica.
    const vistas = new Set<string>();
    const out = origens
      .filter((o) => o.semConvenio)
      .map((o) => {
        vistas.add(o.codigoVerba);
        const p = valorPorVerba.get(o.codigoVerba);
        return {
          codigo: o.codigoOriginal,
          canonico: o.codigoVerba,
          descricao: p?.descricao ?? "",
          valor: p?.valor ?? 0,
          servidores: p?.servidores ?? 0,
          origem: o.origem,
          banco: o.bancoNome,
        };
      });
    for (const [canonico, p] of valorPorVerba) {
      if (vistas.has(canonico)) continue;
      out.push({
        codigo: canonico, canonico, descricao: p.descricao, valor: p.valor,
        servidores: p.servidores, origem: null, banco: null,
      });
    }
    return out.sort((a, b) => b.valor - a.valor || a.codigo.localeCompare(b.codigo));
  }, [origens, q.data]);

  // Fonte indisponível não vira lista vazia: uma fila vazia por engano diz "não
  // há nada a resolver", que é a conclusão mais cara possível aqui.
  if (q.data && q.data.medicao_valida === false) {
    return (
      <div style={{ padding: 12, borderRadius: 10, fontSize: 13, background: "color-mix(in srgb, var(--danger-500) 10%, transparent)", border: "1px solid color-mix(in srgb, var(--danger-500) 30%, transparent)" }}>
        Não foi possível medir a quarentena desta folha{q.data.erro ? ` — ${q.data.erro}` : ""}. A lista abaixo pode estar
        incompleta: <strong>não conclua que não há nada a resolver</strong>.
      </div>
    );
  }
  if (linhas.length === 0) return null;

  const brl = (n: number) => n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
  const total = linhas.reduce((a, l) => a + l.valor, 0);

  return (
    <div style={{ display: "grid", gap: 8, padding: 12, borderRadius: 10, border: "1px solid color-mix(in srgb, var(--gold-500) 35%, transparent)", background: "color-mix(in srgb, var(--gold-500) 7%, transparent)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
        <strong style={{ fontSize: 13 }}>Verbas que nenhum convênio explica</strong>
        {total > 0 ? <span style={{ fontSize: 12, color: "var(--text-dim)" }}>{brl(total)} em quarentena</span> : null}
      </div>
      <p style={{ margin: 0, fontSize: 12, color: "var(--text-muted)", lineHeight: 1.5 }}>
        Declarar a verba no cadastro do convênio da consignatária resolve as duas coisas de uma vez: a folha passa a reconhecer o
        desconto e o que está em quarentena vira dívida daquela consignatária, sem reimportar nada.{" "}
        <Link to="/averbadora/convenios" style={{ color: "var(--gold-500)" }}>Abrir convênios →</Link>
      </p>
      <div style={{ display: "grid", gap: 4 }}>
        {linhas.slice(0, 12).map((l) => (
          <div key={l.canonico} style={{ display: "flex", gap: 8, alignItems: "baseline", fontSize: 12, flexWrap: "wrap" }}>
            <code style={{ fontWeight: 600 }}>{l.codigo}</code>
            <Corta largura={220} titulo={l.descricao}>{l.descricao || "sem descrição na folha"}</Corta>
            {l.valor > 0 ? (
              <span style={{ color: "var(--text-dim)" }}>
                {brl(l.valor)} · {l.servidores} servidor(es)
              </span>
            ) : (
              <span style={{ color: "var(--text-dim)" }}>sem desconto pendente</span>
            )}
            {l.origem ? (
              <span style={{ color: "var(--gold-500)" }}>
                {l.origem === "semeadura" ? "migração" : "classificada à mão"}
                {l.banco ? ` · ${l.banco}` : ""}
              </span>
            ) : null}
          </div>
        ))}
        {linhas.length > 12 ? (
          <span style={{ fontSize: 11, color: "var(--text-dim)" }}>e mais {linhas.length - 12} verba(s)</span>
        ) : null}
      </div>
    </div>
  );
}

function Pendentes({ folhaId, dados, carregando }: { folhaId: number; dados: DadosRubricas; carregando: boolean }) {
  const [vinculando, setVinculando] = useState<AdminRubrica | null>(null);
  const [pagina, setPagina] = useState(1);
  const [porPagina, setPorPagina] = useState(25);

  // A FILA GANHOU BUSCA E RECORTE POR SUGESTAO em 04/09/2026.
  //
  // Ela nao tinha nenhum dos dois: codigo e descricao recebida visiveis, e a
  // unica forma de achar a pendencia de um chamado era percorrer as paginas de
  // uma fila que cresce a cada folha com verba desconhecida.
  //
  // A NATUREZA SUGERIDA E' SUGESTAO, e o rotulo diz isso. Ela vem do
  // consignados e organiza a fila; nao decide classificacao. O dominio exige
  // confirmacao humana, e um chip chamado so' "Natureza" faria a inferencia
  // parecer o fato.
  const [busca, setBusca] = useState("");
  const [sugestao, setSugestao] = useState("");
  const termo = busca.trim().toLowerCase();

  const naFila = useMemo(
    () => (dados?.rubricas ?? []).filter((r) => r.confirmadaEm === null),
    [dados],
  );

  const predicados = useMemo(() => ({
    sugestao: sugestao ? (r: AdminRubrica) => (r.naturezaSugerida ?? "") === sugestao : null,
    busca: termo
      ? (r: AdminRubrica) =>
          r.codigoOriginal.toLowerCase().includes(termo) || r.descricao.toLowerCase().includes(termo)
      : null,
  }), [sugestao, termo]);

  const pendentes = useMemo(
    () => aplicarFiltros(naFila, predicados)
      // Ordem por IMPACTO: mais ocorrencias no topo. E' regra de negocio, nao
      // preferencia — e' o que faz a fila ser trabalhada pelo que mais aparece
      // na folha. Codigo desempata para a paginacao ser estavel.
      .sort((a, b) => b.ocorrencias - a.ocorrencias || a.codigoOriginal.localeCompare(b.codigoOriginal)),
    [naFila, predicados],
  );

  const semSugestao = useMemo(() => aplicarFiltros(naFila, predicados, "sugestao"), [naFila, predicados]);
  const contSugestao = useMemo(() => contagens(semSugestao, (r) => r.naturezaSugerida ?? ""), [semSugestao]);

  const filtros = useFiltros(
    [
      { nome: "busca", ligado: busca !== "", limpar: () => setBusca("") },
      { nome: "naturezaSugerida", ligado: sugestao !== "", limpar: () => setSugestao("") },
    ],
    setPagina,
  );

  // A lista ENCOLHE enquanto se trabalha nela: cada rubrica vinculada sai
  // daqui. Sem o clamp, vincular as ultimas da pagina 3 deixava a pagina 3
  // sem existir e a tabela vazia — parece bug de dado, e o operador nao tem
  // como saber que basta voltar uma pagina. Clampar na leitura (em vez de um
  // efeito que corrige o estado depois) evita o frame vazio no meio.
  const totalPaginas = Math.max(1, Math.ceil(pendentes.length / porPagina));
  const paginaAtual = Math.min(pagina, totalPaginas);
  const pagina1 = (paginaAtual - 1) * porPagina;
  const daPagina = pendentes.slice(pagina1, pagina1 + porPagina);

  const columns: Column<AdminRubrica>[] = [
    { key: "codigo", header: "Código", width: 100, mono: true, render: (r) => <strong>{r.codigoOriginal}</strong> },
    {
      key: "descricao",
      header: "Descrição recebida",
      width: 260,
      render: (r) => <Corta largura={248} titulo={r.descricao}>{r.descricao || "—"}</Corta>,
    },
    {
      key: "sugestao",
      header: "Categoria sugerida",
      width: 170,
      render: (r) => (
        // SUGESTÃO, e o rótulo diz isso: ela vem de onde a verba apareceu (no
        // consignados ou só no holerite), não do texto. Nunca decide.
        <span title="Palpite da descoberta. Quem decide é a pessoa.">
          <Pill variant="pendente">{rotuloSugestao(r.naturezaSugerida)}</Pill>
        </span>
      ),
    },
    {
      key: "vista",
      header: "Vista de",
      width: 150,
      render: (r) => (
        <div style={{ display: "grid", gap: 2, fontSize: 12 }}>
          <span>{fmtCompetencia(r.primeiraCompetencia)}</span>
          {r.ultimaCompetencia && r.ultimaCompetencia !== r.primeiraCompetencia ? (
            <span style={{ fontSize: 11, color: "var(--text-dim)" }}>até {fmtCompetencia(r.ultimaCompetencia)}</span>
          ) : null}
        </div>
      ),
    },
    {
      key: "ocorrencias",
      header: "Ocorrências",
      width: 120,
      align: "right",
      render: (r) => (
        <div style={{ display: "grid", gap: 2 }}>
          <span title="Linhas do holerite na última competência vista.">{r.ocorrencias}</span>
          {r.contratos > 0 ? (
            <span style={{ fontSize: 11, color: "var(--text-dim)" }} title="Contratos que o relatório de conciliação atribui a esta rubrica.">
              {r.contratos} no relatório
            </span>
          ) : null}
        </div>
      ),
    },
    {
      key: "acoes",
      header: "Ações",
      width: 120,
      render: (r) => <Button size="sm" onClick={() => setVinculando(r)}>Vincular</Button>,
    },
  ];

  return (
    <div style={{ display: "grid", gap: 12 }}>
      <SemConvenio folhaId={folhaId} origens={dados?.origens ?? []} />

      <div style={{ padding: 12, borderRadius: 10, fontSize: 13, background: "var(--bg-elev-2)", border: "1px solid var(--border)" }}>
        Enquanto uma rubrica está aqui, o desconto dela <strong>não é atribuído a consignatária alguma</strong> — o valor é abatido de
        a margem de todos os produtos e a linha espera decisão. Classificar é ato humano e fica registrado.
        {/* O ATALHO FICA AQUI, e não no cabeçalho da página: é neste momento — uma
            folha recém-migrada, com o catálogo vazio — que preencher em lote vale
            mais do que digitar uma por uma. Só aparece quando há pendente, porque
            fora dessa situação ele não resolve nada. */}
        {pendentes.length > 2 ? (
          <div style={{ marginTop: 8, paddingTop: 8, borderTop: "1px solid var(--border)" }}>
            <Link to="/averbadora/rubricas/conciliacao" style={{ fontSize: 12, color: "var(--gold-500)" }}>
              São {pendentes.length} rubricas — preencher em lote a partir de um relatório da prefeitura →
            </Link>
          </div>
        ) : null}
      </div>

      {/* A barra so' aparece com fila para recortar. */}
      {naFila.length > 0 ? (
        <FilterBar
          layout="linha"
          searchValue={busca}
          onSearchChange={filtros.filtrar(setBusca)}
          searchPlaceholder="Buscar por código ou descrição recebida"
          {...filtros.propsFilterBar}
          actions={
            Object.keys(contSugestao).filter((k) => k).length > 1 ? (
              <FilterChips
                // "sugerida" NO ROTULO, sempre: e' inferencia do consignados,
                // nao classificacao. O chip organiza a fila; a decisao segue
                // sendo humana e registrada.
                label="Categoria sugerida"
                value={sugestao}
                onChange={filtros.filtrar(setSugestao)}
                options={[
                  { value: "", label: "Todas", count: semSugestao.length },
                  ...Object.entries(contSugestao)
                    .filter(([k]) => k)
                    .sort(([a], [b]) => a.localeCompare(b, "pt-BR"))
                    .map(([k, n]) => ({
                      value: k,
                      label: k ? rotuloSugestao(k as AdminRubricaNatureza) : k,
                      count: n,
                    })),
                ]}
              />
            ) : null
          }
        />
      ) : null}

      <DataTable
        columns={columns}
        rows={daPagina}
        rowKey={(r) => r.id}
        loading={carregando}
        emptyState={
          <EmptyState
            motivo={motivoVazio(naFila.length, pendentes.length)}
            filtroVazio="Nenhuma pendência bate com esse recorte."
            // Base vazia aqui e' BOA NOTICIA — vale dizer, em vez de so'
            // "nenhum registro".
            semDados="Nenhuma rubrica pendente nesta folha — todas foram classificadas."
            onLimpar={filtros.limparFiltros}
          />
        }
      />

      <Pagination
        total={pendentes.length}
        pagina={paginaAtual}
        porPagina={porPagina}
        onPagina={setPagina}
        onPorPagina={setPorPagina}
        rotulo="rubricas"
      />

      {vinculando ? (
        <ModalClassificacao
          folhaId={folhaId}
          rubrica={vinculando}
          vinculoAtivo={null}
          onClose={() => setVinculando(null)}
        />
      ) : null}
    </div>
  );
}


// ============================================================ histórico

/** Toda a corrente de vigências, com quem decidiu cada uma e quando. */
function ModalHistorico({ rubrica, vigencias, onClose }: {
  rubrica: AdminRubrica;
  vigencias: AdminRubricaVinculo[];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [erro, setErro] = useState<string | null>(null);

  const encerrar = useMutation({
    mutationFn: (v: AdminRubricaVinculo) => {
      setErro(null);
      const comp = window.prompt(
        "Encerrar esta vigência a partir de qual competência? (AAAAMM)\n\n" +
        "A partir do mês seguinte, o desconto desta rubrica volta a cair em conciliação " +
        "em vez de virar dívida. Nada do passado é apagado.",
        competenciaAtual(),
      );
      if (!comp || !/^\d{6}$/.test(comp)) throw new Error("Competência inválida — informe AAAAMM.");
      return atlas.admin.rubricas.encerrarVinculo(v.id, comp);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "rubricas"] });
      onClose();
    },
    onError: (e) => setErro(e instanceof Error ? e.message : "Não foi possível encerrar."),
  });

  const ordenadas = [...vigencias].sort((a, b) => b.competenciaInicial.localeCompare(a.competenciaInicial));

  return (
    <Modal title={`Vigências da rubrica ${rubrica.codigoOriginal}`} onClose={onClose} maxWidth={680}>
      <div style={{ display: "grid", gap: 10 }}>
        <p style={{ fontSize: 12, color: "var(--text-muted)", margin: 0 }}>
          Cada linha responde por um período. Trocar consignatária ou produto não edita a linha anterior — abre outra, e o
          passado continua respondendo o que respondia.
        </p>

        {ordenadas.length === 0 ? (
          <div style={{ padding: 20, textAlign: "center", color: "var(--text-muted)", fontSize: 13 }}>
            Esta rubrica ainda não tem vigência — ela está pendente de vínculo.
          </div>
        ) : ordenadas.map((v) => (
          <div
            key={v.id}
            style={{
              padding: 12, borderRadius: 10, background: "var(--bg-elev-2)",
              border: `1px solid ${v.situacao === "ativo" ? "var(--accent)" : "var(--border)"}`,
              display: "grid", gap: 6,
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <strong style={{ fontSize: 13 }}>{fmtVigencia(v)}</strong>
              {v.situacao === "ativo"
                ? <Pill variant="averbado">Vigente</Pill>
                : <Pill variant="expirado">Encerrada</Pill>}
            </div>
            <div style={{ fontSize: 12, display: "grid", gap: 2 }}>
              <span>
                {CATEGORIA_LABEL[v.categoria]}
                {v.tipoServico ? ` · ${rotuloTipoServico(v.tipoServico)}` : ""}
                {v.produto ? ` · ${PRODUTO_LABEL[v.produto]}` : v.categoria === "consignacao_credito" ? " · sem produto" : ""}
              </span>
              {v.consignatariaId != null ? <span style={{ color: "var(--text-muted)" }}>Consignatária #{v.consignatariaId}</span> : null}
              {v.credorFinalId != null ? <span style={{ color: "var(--text-muted)" }}>Credor final #{v.credorFinalId}</span> : null}
            </div>
            <div style={{ fontSize: 11, color: "var(--text-dim)" }}>
              {/* Nome, não id: a tela mostrava "Confirmada por 200". */}
              Confirmada por <b>{v.confirmadoNome || v.confirmadoPor}</b> em{" "}
              {new Date(v.confirmadoEm).toLocaleString("pt-BR")}
              {v.encerradoEm ? (
                <>
                  {" · "}encerrada por <b>{v.encerradoNome || v.encerradoPor}</b> em{" "}
                  {new Date(v.encerradoEm).toLocaleString("pt-BR")}
                </>
              ) : null}
            </div>
            {v.situacao === "ativo" ? (
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <Button size="sm" variant="ghost" onClick={() => encerrar.mutate(v)}>
                  Inativar a partir de…
                </Button>
              </div>
            ) : null}
          </div>
        ))}

        {erro ? (
          <div style={{ padding: 10, borderRadius: 8, fontSize: 13, background: "color-mix(in srgb, var(--danger-500) 12%, transparent)" }}>
            {erro}
          </div>
        ) : null}
      </div>
    </Modal>
  );
}
